// =====================================================================
// src/lib/visitas.js — as decisões de fornecedores e visitas, sem DOM
//
// O módulo `visitas` (0045) tem QUATRO telas debaixo de um nome, e quem
// escolhe é o banco, não a tela — a mesma regra da garagem:
//
//   dono           a imobiliária: os fornecedores DELA e as visitas dos
//                  flats DELA. Agenda; não carimba chegada. "O
//                  proprietário não enxerga as visitas dos outros
//                  apartamentos" (Hugo, 20/09) vale agora para ela (0100,
//                  2A). Até a 0100 esta visão era também a do login de
//                  dono, que saiu na 0101.
//   balcao         administração e recepção (`editar visitas`): as
//                  visitas do dia, a chegada, a saída, o cancelamento,
//                  e a tabela de fornecedores. Cadastrar fornecedor DO
//                  HOTEL é só da administração (0046) — `podeCadastrar`.
//   portaria       quem só VÊ visitas: a lista do dia, sem botão.
//   null           sem o módulo, não há tela.
//
// Como em `garagem.js`: `visaoDe()` escolhe o DESENHO; se ela errasse, o
// banco continuaria recusando — tela some com um inspetor aberto.
// =====================================================================

// ── QUEM VÊ O QUÊ ────────────────────────────────────────────────────
// A imobiliária vem primeiro, antes do nível — identidade, não cargo
// (0039): `dono_do_fornecedor` (0045) devolve 'imobiliaria' para ela ANTES
// de olhar o nível. Ela entra com `ver visitas` (semente da 0101) e agenda
// e cancela pela identidade (0100).
export function visaoDe(contexto) {
  const c = contexto || {};
  const m = (Array.isArray(c.modulos) ? c.modulos : []).find(x => x && x.chave === 'visitas');
  if (!m) return null;
  if (c.imobiliaria && c.imobiliaria.id) return 'dono';
  return m.nivel === 'editar' ? 'balcao' : 'portaria';
}

// A legenda debaixo do título (Hugo "4 A"). Nulo = a regra padrão.
export function legendaDe(contexto) {
  const v = visaoDe(contexto);
  if (v === 'portaria') return 'Visitas do dia';
  if (v === 'dono') return 'Visitas dos seus flats';
  return null;
}

// Espelho de `salvar_fornecedor` depois da 0046: fornecedor do hotel é
// cadastrado por quem tem `editar configuracoes` — a administração e o
// papel `ti`. A imobiliária cadastra os dela sempre. A recepção agenda e recebe,
// mas não cadastra. Isto acende ou apaga o botão; quem recusa é o banco.
export function podeCadastrarFornecedor(contexto) {
  const v = visaoDe(contexto);
  if (v === 'dono') return true;
  if (v !== 'balcao') return false;
  const cfg = (contexto.modulos || []).find(x => x && x.chave === 'configuracoes');
  return !!cfg && cfg.nivel === 'editar';
}

// ── DE QUEM É O FORNECEDOR ───────────────────────────────────────────
// `fornecedores_do_cliente` devolve `de_quem`: 'imobiliaria' ou 'hotel'
// (0101: o fornecedor do proprietário saiu — produção tinha zero). O rótulo
// é para a tabela do balcão, onde os dois aparecem misturados.
export function rotuloDeQuem(deQuem) {
  return deQuem === 'imobiliaria' ? 'Imobiliária'
       : deQuem === 'hotel' ? 'Hotel' : '—';
}

// O lápis da tabela de fornecedores. Espelho do banco (0045/0046,
// `fornecedor_para_editar`): a imobiliária edita só os DELA; no balcão, o
// do hotel só quem cadastra fornecedor do hotel. O de imobiliária não é da
// recepção nem da administração — o banco recusaria.
export function podeEditarFornecedor(f, visao, podeCadastrar) {
  if (!f) return false;
  if (visao === 'dono') return f.de_quem === 'imobiliaria';
  return visao === 'balcao' && f.de_quem === 'hotel' && !!podeCadastrar;
}

// ── A SITUAÇÃO DA VISITA ─────────────────────────────────────────────
// Quatro estados (0045), com as cores do resto do app: a agendada
// aguarda, a que chegou está no prédio (vermelho como a vaga ocupada), a
// que saiu está encerrada, a cancelada é cinza.
export function situacaoDaVisita(v) {
  const s = v && v.situacao;
  if (s === 'chegou')    return { chave: 'chegou',    rotulo: 'No prédio',  classe: 'est-a_limpar',            cor: '#dc2626' };
  if (s === 'saiu')      return { chave: 'saiu',      rotulo: 'Saiu',       classe: 'est-liberado',            cor: '#16a34a' };
  if (s === 'cancelada') return { chave: 'cancelada', rotulo: 'Cancelada',  classe: 'est-sem_movimento',       cor: '#6b7280' };
  return                        { chave: 'agendada',  rotulo: 'Agendada',   classe: 'est-aguardando_inspecao', cor: '#d97706' };
}

// Quais verbos cabem a cada visita, para cada visão. A chegada é da
// RECEPÇÃO (0045: "ele deve se dirigir a recepcao - para cadastro e
// liberacao entrada"); o dono agenda e cancela o que ainda não chegou.
// 0091 (decisão 4A do Hugo): "Liberar entrada" só na visita de HOJE — a de
// outro dia a recepção remarca (o banco recusa a chegada fora do dia).
// `hoje` é o dia do HOTEL (hojeDe), nunca o do computador.
export function acoesDaVisita(v, visao, hoje) {
  const s = (v && v.situacao) || 'agendada';
  if (visao === 'balcao') {
    if (s === 'agendada') return v && v.data && hoje && v.data !== hoje ? ['remarcar', 'cancelar'] : ['chegada', 'cancelar'];
    if (s === 'chegou')   return ['saida'];
    return [];
  }
  if (visao === 'dono' && s === 'agendada') return ['cancelar'];
  return [];
}

// ── A JANELA, EM TEXTO ───────────────────────────────────────────────
// `time` chega como 'HH:MM:SS'; a tela mostra 'HH:MM'. Sem janela, "o
// dia todo". Desde a 0047 a janela TRANCA o portão enquanto a visita
// está agendada (Hugo "3 A"); depois que a recepção libera, abre no dia.
export function textoDaJanela(de, ate) {
  const h = t => (t ? String(t).slice(0, 5) : null);
  const a = h(de), b = h(ate);
  if (a && b) return `${a} às ${b}`;
  if (a) return `a partir de ${a}`;
  if (b) return `até ${b}`;
  return 'o dia todo';
}

// ── ANTES DE MANDAR ──────────────────────────────────────────────────
// O banco recusa de qualquer forma (0045). Isto é só para não fazer a
// pessoa preencher tudo e levar um erro depois.
export function validarAgendamento({ fornecedor, data, janela_de, janela_ate, unidade }, { hoje, dono } = {}) {
  const erros = [];
  if (!fornecedor) erros.push('Escolha o fornecedor');
  if (!data) erros.push('Informe a data da visita');
  else if (hoje && String(data) < String(hoje)) erros.push('Visita no passado — confira a data');
  if (janela_de && janela_ate && String(janela_ate) < String(janela_de)) erros.push('A janela termina antes de começar');
  // A imobiliária só agenda num flat dela; visita ao prédio é do hotel (0100).
  if (dono && !unidade) erros.push('Escolha o quarto');
  return erros;
}

// A pessoa que sobe ao apartamento tem nome E sobrenome — "sempre
// utilize nome e sobrenome na identificacao … em qualquer situacao"
// (Hugo, 19/09). Vale para o visitante como vale para o hóspede. Com
// uma pessoa reaproveitada do cadastro, os campos não precisam vir.
export function validarChegada({ pessoa, nome, sobrenome }) {
  if (pessoa) return [];
  const erros = [];
  if (!String(nome || '').trim()) erros.push('Nome é obrigatório');
  if (!String(sobrenome || '').trim()) erros.push('Informe o sobrenome');
  return erros;
}

// ── O QUE VAI PARA O BANCO ───────────────────────────────────────────
// Só o que foi preenchido. Campo vazio vira null, não string vazia — o
// banco guarda `nullif(btrim(...), '')` de qualquer jeito, mas mandar
// null diz na rede o que se quis dizer.
export function argumentosDoAgendamento(f) {
  const ou = x => (x == null || String(x).trim() === '' ? null : String(x).trim());
  return {
    p_fornecedor: f.fornecedor, p_data: f.data, p_unidade: ou(f.unidade),
    p_janela_de: ou(f.janela_de), p_janela_ate: ou(f.janela_ate),
    p_motivo: ou(f.motivo), p_placa: ou(f.placa),
  };
}

// ── A BUSCA DE REPETIDOS ─────────────────────────────────────────────
// `visitantes_repetidos` (0045) devolve a forma do FORMULÁRIO — nome sem
// o sobrenome, sobrenome à parte (CLAUDE.md §5.1). Vai como está.
export function preencherDeVisitante(p) {
  return { pessoa: p.id, nome: p.nome || '', sobrenome: p.sobrenome || '',
           documento: p.documento || '', tipo_documento: p.tipo_documento || '', telefone: p.telefone || '' };
}

export function rotuloVisitante(p) {
  const nome = [p.nome, p.sobrenome].filter(Boolean).join(' ');
  return p.documento ? `${nome} · ${p.documento}` : nome;
}

// ── FILTRAR A TABELA ─────────────────────────────────────────────────
export function filtrarFornecedores(lista, { texto = '', comInativos = false } = {}) {
  const t = String(texto || '').trim().toLowerCase();
  return (lista || []).filter(f => (comInativos || f.ativo !== false)
    && (!t || [f.nome, f.ramo, f.documento, f.telefone].some(x => String(x || '').toLowerCase().includes(t))));
}
